import { apiFetch } from '../auth/api';

export type TaskStatus = 'open' | 'in_progress' | 'completed' | 'cancelled';
export type TaskPriority = 'low' | 'normal' | 'high';

export interface Task {
  id: number;
  team_id: number;
  transaction_id: number | null;
  lead_id: number | null;
  template_id: number | null;
  phase_slug: string | null;
  phase_name: string | null;
  phase_sort_order: number | null;
  template_sort_order: number | null;
  assignee_user_id: number | null;
  assignee_first: string | null;
  assignee_last: string | null;
  title: string;
  description: string | null;
  /** L-79 (Daniel 2026-07-04): richer multi-line instructions + links
   *  to team resources. Copied from task_templates.details when the
   *  task is generated. Nullable for legacy rows. */
  details: string | null;
  /** L-79b: task.details when set, else falls back to the linked
   *  template's current details. Displayed on the TaskList row expand. */
  effective_details?: string | null;
  /** L-120 (Daniel 2026-07-22): SOP link. */
  sop_url?: string | null;
  /** L-120: task.sop_url when set, else the linked template's sop_url. */
  effective_sop_url?: string | null;
  due_date: number | null;
  status: TaskStatus;
  priority: TaskPriority;
  /** K-2: numeric 1-9 rank (1 = highest). Default 5. Layered on top of
   *  the legacy text priority for the Brivity-style priority chip. */
  priority_rank: number;
  completed_at: number | null;
  created_at: number;
  updated_at: number;
  /** Effective email/text content — instance override OR template fallback. Null when neither exists. */
  effective_email_subject?: string | null;
  effective_email_body?: string | null;
  effective_text_body?: string | null;
  /** Per-step channel flags (from the template). Pill renders if this is true
   *  OR if the corresponding body content exists. */
  needs_call?: number;
  needs_text?: number;
  needs_email?: number;
  /** When the agent marked the call/text/email portion done. Null when not yet
   *  done. Used to show a "Called ✓" indicator independent of task status. */
  call_completed_at?: number | null;
  text_completed_at?: number | null;
  email_completed_at?: number | null;
  /** L-36 (Daniel 2026-06-27): multi-assignee. Empty array = unassigned.
   *  The legacy single-column assignee_user_id is kept in sync with the
   *  first element here, so old code that reads it keeps working. */
  assignees: Array<{ id: number; first_name: string; last_name: string | null }>;
}

export function listTransactionTasks(txId: number): Promise<{ tasks: Task[] }> {
  return apiFetch(`/api/transactions/${txId}/tasks`);
}

/**
 * Tasks across the whole team, joined with transaction info. Used by the Team
 * Tasks page. Includes the same effective_email_*, effective_text_body fields
 * as the per-transaction list so quick-send buttons work the same way.
 */
export interface TeamTask extends Task {
  tx_address_street: string | null;
  tx_address_city: string | null;
  tx_address_state: string | null;
  tx_address_zip: string | null;
  tx_type: 'auction' | 'listing' | 'buyer' | null;
  tx_status: 'active' | 'under_contract' | 'closed' | 'cancelled' | null;
  tx_current_phase_slug: string | null;
  tx_list_price: number | null;
  tx_sale_price: number | null;
  tx_contract_date: number | null;
  tx_close_date: number | null;
  tx_primary_agent_id: number | null;
  /**
   * R8-1 (Daniel 2026-08-05): the lead behind a lead follow-up.
   *
   * `tasks.lead_id` has been a legal column since migration 0002 but nothing
   * ever wrote one, so every row on this list had a transaction and the "Deal"
   * column could read `transaction_id` unconditionally. Lead follow-ups changed
   * that: they have a person and no address, and without these fields the row
   * linked to /transactions/null.
   *
   * All null on a transaction task. Both are present or both are absent, which
   * is what makes `lead_id != null` a safe test for "this is a lead follow-up".
   */
  lead_first: string | null;
  lead_last: string | null;
  lead_stage: string | null;
  lead_target_month: number | null;
  lead_target_year: number | null;
  lead_intent: 'buy' | 'sell' | 'both' | null;
}

export interface ListTasksFilters {
  /** "me" or a user_id; omit for all */
  assignee?: 'me' | number | '';
  status?: TaskStatus | '';
  due_before?: number;
}

export function listTeamTasks(filters: ListTasksFilters = {}): Promise<{ tasks: TeamTask[] }> {
  const params = new URLSearchParams();
  if (filters.assignee !== undefined && filters.assignee !== '') {
    params.set('assignee', String(filters.assignee));
  }
  if (filters.status) params.set('status', filters.status);
  if (filters.due_before) params.set('due_before', String(filters.due_before));
  const qs = params.toString();
  return apiFetch(`/api/tasks${qs ? `?${qs}` : ''}`);
}

export interface CreateTaskBody {
  title: string;
  description?: string;
  details?: string;
  sop_url?: string;
  due_date?: number | null;
  assignee_user_id?: number | null;
  phase_slug?: string;
  priority?: TaskPriority;
}

export function createTask(txId: number, body: CreateTaskBody): Promise<{ ok: true; id: number }> {
  return apiFetch(`/api/transactions/${txId}/tasks`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export interface UpdateTaskBody {
  title?: string;
  description?: string | null;
  details?: string | null;
  sop_url?: string | null;
  due_date?: number | null;
  assignee_user_id?: number | null;
  /** L-36: full multi-assignee replacement. Pass an array of user ids
   *  (empty array = unassigned). Server resyncs both task_assignees
   *  and the legacy assignee_user_id (set to the first entry). */
  assignees?: number[];
  priority?: TaskPriority;
  /** K-2 numeric priority (1-9, 1 = highest). */
  priority_rank?: number;
  status?: TaskStatus;
  /** Tri-state: true stamps unixepoch(); false/null clears; omit = unchanged. */
  call_completed?: boolean | null;
  text_completed?: boolean | null;
  email_completed?: boolean | null;
}

export function updateTask(id: number, patch: UpdateTaskBody): Promise<{ ok: true }> {
  return apiFetch(`/api/tasks/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });
}

/** Soft-delete many tasks at once (recoverable — sets deleted_at). */
export function bulkDeleteTasks(ids: number[]): Promise<{ ok: true; deleted: number }> {
  return apiFetch('/api/tasks/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
}

/** Mark many tasks complete at once. */
export function bulkCompleteTasks(ids: number[]): Promise<{ ok: true; completed: number }> {
  return apiFetch('/api/tasks/bulk-complete', { method: 'POST', body: JSON.stringify({ ids }) });
}

/** Move many tasks to a new due date. `null` clears the date.
 *  (Daniel 2026-07-27 audit — the overdue list could only Complete or
 *  Delete, but the real response to overdue work is move it or hand it off.) */
export function bulkRescheduleTasks(ids: number[], dueDate: number | null): Promise<{ ok: true; rescheduled: number }> {
  return apiFetch('/api/tasks/bulk-reschedule', {
    method: 'POST',
    body: JSON.stringify({ ids, due_date: dueDate }),
  });
}

/** Hand many tasks to one person. `null` unassigns. */
export function bulkAssignTasks(ids: number[], assigneeUserId: number | null): Promise<{ ok: true; assigned: number }> {
  return apiFetch('/api/tasks/bulk-assign', {
    method: 'POST',
    body: JSON.stringify({ ids, assignee_user_id: assigneeUserId }),
  });
}

export function deleteTask(id: number): Promise<{ ok: true }> {
  return apiFetch(`/api/tasks/${id}`, { method: 'DELETE' });
}

// ---------------------------------------------------------------------
// Phase advancement
// ---------------------------------------------------------------------

/**
 * Build 10.1 (team 2026-09-09): clone the current phase's templates onto a
 * deal that shows "No steps". Safe to repeat; already-cloned templates skip.
 */
export function generatePhaseTasks(
  txId: number,
  /** Build 16: any phase on the deal's track. Omit for the current phase. */
  phaseSlug?: string,
): Promise<{ ok: true; phase_slug: string; tasks_generated: number; skipped: number }> {
  return apiFetch(`/api/transactions/${txId}/generate-phase-tasks`, {
    method: 'POST',
    body: JSON.stringify(phaseSlug ? { phase_slug: phaseSlug } : {}),
  });
}

export function advancePhase(
  txId: number,
  notes?: string,
  completeOpenTasks?: boolean,
): Promise<{ ok: true; phase_slug: string; tasks_generated: number; prior_tasks_completed: number }> {
  return apiFetch(`/api/transactions/${txId}/advance-phase`, {
    method: 'POST',
    body: JSON.stringify({ notes, complete_open_tasks: !!completeOpenTasks }),
  });
}

/**
 * Build 66: contact tasks (the contact_tasks table) for the Tasks page.
 * Complete or snooze them through updateContactTask in api/contacts.ts.
 */
export interface TeamContactTask {
  id: number;
  contact_id: number;
  title: string;
  description: string | null;
  due_date: number | null;
  status: 'open' | 'in_progress' | 'completed' | 'cancelled';
  priority: 'low' | 'normal' | 'high';
  assignee_user_id: number | null;
  assignee_first: string | null;
  assignee_last: string | null;
  contact_first: string;
  contact_last: string | null;
  contact_phone: string | null;
  completed_at: number | null;
  created_at: number;
}
export function listTeamContactTasks(opts: { assignee?: 'me' | number | ''; status?: string }): Promise<{ tasks: TeamContactTask[] }> {
  const p = new URLSearchParams();
  if (opts.assignee) p.set('assignee', String(opts.assignee));
  if (opts.status) p.set('status', opts.status);
  const qs = p.toString();
  return apiFetch(`/api/tasks/contact${qs ? `?${qs}` : ''}`);
}
